/**
 * Purview CVC Customers Tab Component
 * Displays customer engagement data from Purview CVC program with rich dashboard design
 */

import { Callout, Checkbox, DirectionalHint, Icon, IconButton, Link, SearchBox, Spinner, SpinnerSize, TooltipHost } from "@fluentui/react";
import { useBoolean } from "@fluentui/react-hooks";
import { LinkRegular } from "@fluentui/react-icons";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import { CustomDataGrid } from "../../SharedComponents/CustomDataGrid/CustomDataGrid";
import { IRows } from "../../SharedComponents/Interfaces/CustomDataGrid.interfaces";
import { useGetPurviewCVCCustomersQuery } from "../../slices/PurviewCVC.slice";
import {
    IPurviewCVCCustomersTabProps,
    IPurviewCVCCustomer,
    IPurviewCVCChartData,
    ILastEngagedBucket,
    IPurviewCVCInlineNote
} from "./PurviewCVC.interfaces";
import { PURVIEW_CVC_CUSTOMERS_STYLES, PURVIEW_CVC_STYLES } from "./PurviewCVC.styles";
import { SOLUTION_COLORS, ENGAGEMENT_STATUS_COLORS, LAST_ENGAGED_BUCKETS, CVC_FILTER_STORAGE_PREFIX } from "./PurviewCVC.constants";
import PurviewCVCNotesPanel from "./PurviewCVCNotesPanel";
import PurviewCVCAICompanionSection from "./PurviewCVCAICompanionSection";
import FluentDonutChart from "../../ChartComponents/FluentDountChart";
import CustomCard from "../../SharedComponents/CustomCard";
import { getNextColor } from "@fluentui/react-charting";

/**
 * Searchable Multi-Select Dropdown Component
 */
interface SearchableMultiSelectProps {
    placeholder: string;
    options: string[];
    selectedKeys: string[];
    onChange: (selectedKeys: string[]) => void;
    width?: string;
}

const SearchableMultiSelect: React.FC<SearchableMultiSelectProps> = ({
    placeholder,
    options,
    selectedKeys,
    onChange,
    width = "100%"
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchText, setSearchText] = useState("");
    const buttonRef = useRef<HTMLDivElement>(null);
    const calloutRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!isOpen) return;

        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            const isClickInsideButton = buttonRef.current?.contains(target);
            const isClickInsideCallout = calloutRef.current?.contains(target);

            if (!isClickInsideButton && !isClickInsideCallout) {
                setIsOpen(false);
                setSearchText("");
            }
        };

        const timeoutId = setTimeout(() => {
            document.addEventListener("mousedown", handleClickOutside);
        }, 0);

        return () => {
            clearTimeout(timeoutId);
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isOpen]);

    const filteredOptions = useMemo(() => {
        if (!searchText) return options;
        const lowerSearch = searchText.toLowerCase();
        return options.filter((opt) => opt.toLowerCase().includes(lowerSearch));
    }, [options, searchText]);

    const handleToggle = (option: string) => {
        if (selectedKeys.includes(option)) {
            onChange(selectedKeys.filter((k) => k !== option));
        } else {
            onChange([...selectedKeys, option]);
        }
    };

    const displayText = selectedKeys.length > 0
        ? selectedKeys.length === 1
            ? selectedKeys[0]
            : `${selectedKeys.length} selected`
        : placeholder;

    return (
        <div style={{ width, position: "relative" }}>
            <div
                ref={buttonRef}
                onClick={() => setIsOpen(!isOpen)}
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "0 12px",
                    height: "36px",
                    background: "#FFFFFF",
                    border: "1px solid #E1DFDD",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontSize: "13px",
                    color: selectedKeys.length > 0 ? "#333" : "#666",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap"
                }}
            >
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {displayText}
                </span>
                <Icon iconName="ChevronDown" style={{ fontSize: 12, color: "#605E5C", marginLeft: 8, flexShrink: 0 }} />
            </div>

            {isOpen && (
                <Callout
                    target={buttonRef.current}
                    onDismiss={() => {
                        setIsOpen(false);
                        setSearchText("");
                    }}
                    directionalHint={DirectionalHint.bottomLeftEdge}
                    isBeakVisible={false}
                    gapSpace={2}
                    setInitialFocus={false}
                    styles={{
                        root: { boxShadow: "0 4px 16px rgba(0,0,0,0.12)", borderRadius: "8px" },
                        calloutMain: { borderRadius: "8px", minWidth: 200, maxWidth: 300 }
                    }}
                >
                    <div ref={calloutRef} style={{ padding: "8px" }}>
                        <SearchBox
                            placeholder="Search..."
                            value={searchText}
                            onChange={(_, newValue) => setSearchText(newValue || "")}
                            styles={{
                                root: { marginBottom: 8 },
                                field: { fontSize: 13 }
                            }}
                            autoFocus
                        />
                        <div style={{ maxHeight: 250, overflowY: "auto" }}>
                            {filteredOptions.length === 0 ? (
                                <div style={{ padding: "8px 12px", color: "#666", fontSize: 13 }}>No matches found</div>
                            ) : (
                                filteredOptions.map((option) => (
                                    <div
                                        key={option}
                                        onClick={() => handleToggle(option)}
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            padding: "8px 12px",
                                            cursor: "pointer",
                                            background: selectedKeys.includes(option) ? "#e6f2fb" : "transparent",
                                            borderRadius: 4
                                        }}
                                        onMouseEnter={(e) => {
                                            if (!selectedKeys.includes(option)) {
                                                e.currentTarget.style.background = "#f3f2f1";
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.background = selectedKeys.includes(option) ? "#e6f2fb" : "transparent";
                                        }}
                                    >
                                        <Checkbox
                                            checked={selectedKeys.includes(option)}
                                            styles={{ root: { marginRight: 8, pointerEvents: "none" } }}
                                        />
                                        <span style={{ fontSize: 13, color: "#333" }}>{option || "(Blank)"}</span>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </Callout>
            )}
        </div>
    );
};

/**
 * Status Badge Component
 */
const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
    const config = ENGAGEMENT_STATUS_COLORS[status] || ENGAGEMENT_STATUS_COLORS["Active"];
    return (
        <div
            style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "3px 8px",
                borderRadius: "999px",
                background: config.background,
                color: config.color,
                fontSize: "13px",
                lineHeight: "18px",
                letterSpacing: "0.2px",
                minWidth: "72px"
            }}
        >
            {status}
        </div>
    );
};

/**
 * Stat Card Component
 */
const StatCard: React.FC<{
    icon: string;
    iconColor: string;
    iconBgColor: string;
    value: number | string;
    label: string;
    change?: number;
}> = ({ icon, iconColor, iconBgColor, value, label, change }) => (
    <div
        style={{
            background: "#FFFFFF",
            borderRadius: "12px",
            padding: "16px 20px",
            display: "flex",
            alignItems: "center",
            gap: "16px",
            border: "1px solid #E6E9ED",
            flex: 1,
            minWidth: "200px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
            position: "relative"
        }}
    >
        <TooltipHost
            content={label}
            styles={{
                root: {
                    position: "absolute",
                    top: 5,
                    right: 5,
                    display: "inline-flex"
                }
            }}
        >
            <IconButton
                ariaLabel={`Information about ${label}`}
                iconProps={{ iconName: "Info" }}
                styles={{
                    root: {
                        borderRadius: 6
                    }
                }}
            />
        </TooltipHost>
        <div
            style={{
                width: "48px",
                height: "48px",
                borderRadius: "10px",
                background: iconBgColor,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "22px",
                color: iconColor,
                flexShrink: 0
            }}
        >
            <Icon iconName={icon} />
        </div>
        <div style={{ display: "flex", flexDirection: "column" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <span
                    style={{
                        fontSize: "28px",
                        fontWeight: 700,
                        color: "#1A2A45",
                        lineHeight: 1.2
                    }}
                >
                    {value}
                </span>
                {change !== undefined && change > 0 && (
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                            padding: "4px 10px",
                            borderRadius: "20px",
                            background: "linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)",
                            boxShadow: "0 2px 6px rgba(39, 174, 96, 0.2)"
                        }}
                    >
                        <Icon
                            iconName="ChevronUpSmall"
                            style={{
                                fontSize: "12px",
                                fontWeight: 700,
                                color: "#27ae60"
                            }}
                        />
                        <span
                            style={{
                                fontSize: "13px",
                                fontWeight: 600,
                                color: "#27ae60"
                            }}
                        >
                            +{change}
                        </span>
                    </div>
                )}
            </div>
            <span
                style={{
                    fontSize: "13px",
                    color: "#7f8c8d",
                    marginTop: "2px"
                }}
            >
                {label}
            </span>
        </div>
    </div>
);

/**
 * Calculate days since last engaged
 */
const calculateDaysSinceLastEngaged = (dateStr: string | null): number => {
    if (!dateStr) return -1;
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return -1;
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

/**
 * Get last engaged bucket for a date
 */
const getLastEngagedBucket = (dateStr: string | null): string => {
    const days = calculateDaysSinceLastEngaged(dateStr);
    if (days === -1) return "Unknown";

    for (const bucket of LAST_ENGAGED_BUCKETS) {
        if (bucket.key === "Unknown") continue;
        if (days >= bucket.minDays && days <= bucket.maxDays) {
            return bucket.key;
        }
    }
    return "Unknown";
};

/**
 * Donut Chart Filters Component
 * Uses CustomCard wrapper matching BWC style
 */
const DonutChartFilters: React.FC<{
    cvcLeadData: IPurviewCVCChartData[];
    selectedCVCLeads: string[];
    onCVCLeadClick: (leads: string[]) => void;
    solutionData: IPurviewCVCChartData[];
    selectedSolutions: string[];
    onSolutionClick: (solutions: string[]) => void;
    lastEngagedData: ILastEngagedBucket[];
    selectedLastEngaged: string[];
    onLastEngagedClick: (keys: string[]) => void;
    totalEngagements: number;
}> = ({
    cvcLeadData,
    selectedCVCLeads,
    onCVCLeadClick,
    solutionData,
    selectedSolutions,
    onSolutionClick,
    lastEngagedData,
    selectedLastEngaged,
    onLastEngagedClick,
    totalEngagements
}) => {
        // CVC Lead chart data
        const cvcLeadChartData = useMemo(() => {
            return cvcLeadData.map((item, index) => ({
                legend: item.stage,
                data: item.count,
                color: getNextColor(index),
                onClick: () => {
                    if (selectedCVCLeads.includes(item.stage)) {
                        onCVCLeadClick(selectedCVCLeads.filter((s) => s !== item.stage));
                    } else {
                        onCVCLeadClick([...selectedCVCLeads, item.stage]);
                    }
                }
            }));
        }, [cvcLeadData, selectedCVCLeads, onCVCLeadClick]);

        // Solution chart data
        const solutionChartData = useMemo(() => {
            return solutionData.map((item, index) => ({
                legend: item.stage,
                data: item.count,
                color: SOLUTION_COLORS[item.stage] || getNextColor(index + 5),
                onClick: () => {
                    if (selectedSolutions.includes(item.stage)) {
                        onSolutionClick(selectedSolutions.filter((s) => s !== item.stage));
                    } else {
                        onSolutionClick([...selectedSolutions, item.stage]);
                    }
                }
            }));
        }, [solutionData, selectedSolutions, onSolutionClick]);

        // Last Engaged chart data
        const lastEngagedChartData = useMemo(() => {
            return lastEngagedData.map((bucket) => ({
                legend: bucket.label,
                data: bucket.count,
                color: bucket.color,
                onClick: () => {
                    if (selectedLastEngaged.includes(bucket.key)) {
                        onLastEngagedClick(selectedLastEngaged.filter((k) => k !== bucket.key));
                    } else {
                        onLastEngagedClick([...selectedLastEngaged, bucket.key]);
                    }
                }
            }));
        }, [lastEngagedData, selectedLastEngaged, onLastEngagedClick]);

        return (
            <div style={{ display: "flex", gap: "16px", flexWrap: "wrap", marginBottom: "16px" }}>
                {/* Engagements by CVC Lead */}
                <CustomCard
                    cardHeaderTitle="Engagements by CVC Lead"
                    styles={{ root: { flex: 1, minWidth: 350, minHeight: 320 } }}
                >
                    {selectedCVCLeads.length > 0 && (
                        <div style={{ position: "absolute", top: 12, right: 12 }}>
                            <span
                                style={{
                                    fontSize: "12px",
                                    color: "#0078D4",
                                    cursor: "pointer",
                                    textDecoration: "underline"
                                }}
                                onClick={() => onCVCLeadClick([])}
                            >
                                Clear Filter ({selectedCVCLeads.length})
                            </span>
                        </div>
                    )}
                    <div style={{ minHeight: 280, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <FluentDonutChart
                            data={{
                                chartTitle: "Engagements by CVC Lead",
                                chartData: cvcLeadChartData
                            }}
                            height={230}
                            width={300}
                            innerRadius={50}
                            valueInsideDonut={totalEngagements}
                        />
                    </div>
                </CustomCard>

                {/* Solution by Engagements */}
                <CustomCard
                    cardHeaderTitle="Solution by Engagements"
                    styles={{ root: { flex: 1, minWidth: 350, minHeight: 320 } }}
                >
                    {selectedSolutions.length > 0 && (
                        <div style={{ position: "absolute", top: 12, right: 12 }}>
                            <span
                                style={{
                                    fontSize: "12px",
                                    color: "#0078D4",
                                    cursor: "pointer",
                                    textDecoration: "underline"
                                }}
                                onClick={() => onSolutionClick([])}
                            >
                                Clear Filter ({selectedSolutions.length})
                            </span>
                        </div>
                    )}
                    <div style={{ minHeight: 280, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <FluentDonutChart
                            data={{
                                chartTitle: "Solution by Engagements",
                                chartData: solutionChartData
                            }}
                            height={230}
                            width={300}
                            innerRadius={50}
                            valueInsideDonut={totalEngagements}
                        />
                    </div>
                </CustomCard>

                {/* Last Engaged (days since latest notes) */}
                <CustomCard
                    cardHeaderTitle="Last Engaged (days since latest notes)"
                    styles={{ root: { flex: 1, minWidth: 350, minHeight: 320 } }}
                >
                    {selectedLastEngaged.length > 0 && (
                        <div style={{ position: "absolute", top: 12, right: 12 }}>
                            <span
                                style={{
                                    fontSize: "12px",
                                    color: "#059669",
                                    cursor: "pointer",
                                    textDecoration: "underline"
                                }}
                                onClick={() => onLastEngagedClick([])}
                            >
                                Clear Filter ({selectedLastEngaged.length})
                            </span>
                        </div>
                    )}
                    <div style={{ minHeight: 280, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <FluentDonutChart
                            data={{
                                chartTitle: "Last Engaged",
                                chartData: lastEngagedChartData
                            }}
                            height={230}
                            width={300}
                            innerRadius={50}
                            valueInsideDonut={totalEngagements}
                        />
                    </div>
                </CustomCard>
            </div>
        );
    };

/**
 * Main Purview CVC Customers Tab Component
 */
export const PurviewCVCCustomersTab: React.FC<IPurviewCVCCustomersTabProps> = ({ isLoading: externalLoading }) => {
    // API query
    const { data: customersResponse, isLoading: isLoadingCustomers, error } = useGetPurviewCVCCustomersQuery();

    // Filter state with localStorage persistence
    const [selectedCustomers, setSelectedCustomers] = useLocalStorage<string[]>(`${CVC_FILTER_STORAGE_PREFIX}-customers`, []);
    const [selectedSolutions, setSelectedSolutions] = useLocalStorage<string[]>(`${CVC_FILTER_STORAGE_PREFIX}-solutions`, []);
    const [selectedCVCLeads, setSelectedCVCLeads] = useLocalStorage<string[]>(`${CVC_FILTER_STORAGE_PREFIX}-cvcleads`, []);
    const [selectedCustomerAssignedPMs, setSelectedCustomerAssignedPMs] = useLocalStorage<string[]>(`${CVC_FILTER_STORAGE_PREFIX}-assignedpms`, []);
    const [selectedLastEngaged, setSelectedLastEngaged] = useLocalStorage<string[]>(`${CVC_FILTER_STORAGE_PREFIX}-lastengaged`, []);
    const [searchQuery, setSearchQuery] = useState("");

    // Notes panel state
    const [isNotesPanelOpen, { setTrue: openNotesPanel, setFalse: closeNotesPanel }] = useBoolean(false);
    const [isLoadingNotes, setIsLoadingNotes] = useState(false);
    const [selectedCustomerForNotes, setSelectedCustomerForNotes] = useState<{
        customerProductId: string;
        customerName: string;
        productName: string;
        notesData: IPurviewCVCInlineNote[];
    } | null>(null);

    // Get unique filter options
    const filterOptions = useMemo(() => {
        if (!customersResponse?.customers) return {
            customers: [],
            solutions: [],
            cvcLeads: [],
            customerAssignedPMs: []
        };

        const customers = Array.from(new Set(customersResponse.customers.map(c => c.crmCustomerName))).filter(Boolean).sort() as string[];
        const solutions = Array.from(new Set(customersResponse.customers.map(c => c.crmProductName))).filter(Boolean).sort() as string[];
        // CVC Lead uses the cvcLead field from Kusto
        const cvcLeads = Array.from(new Set(customersResponse.customers.map(c => c.cvcLead))).filter(Boolean).sort() as string[];
        // Customer Assigned PM uses the owner field
        const customerAssignedPMs = Array.from(new Set(customersResponse.customers.map(c => c.owner))).filter(Boolean).sort() as string[];

        return { customers, solutions, cvcLeads, customerAssignedPMs };
    }, [customersResponse]);

    // Filter customers based on selections
    const filteredCustomers = useMemo(() => {
        if (!customersResponse?.customers) return [];

        return customersResponse.customers.filter(customer => {
            // Search filter
            if (searchQuery) {
                const query = searchQuery.toLowerCase();
                const matchesSearch =
                    customer.crmCustomerName?.toLowerCase().includes(query) ||
                    customer.crmProductName?.toLowerCase().includes(query) ||
                    customer.cvcLead?.toLowerCase().includes(query) ||
                    customer.owner?.toLowerCase().includes(query) ||
                    customer.latestNote?.toLowerCase().includes(query);
                if (!matchesSearch) return false;
            }

            // Customer filter
            if (selectedCustomers.length > 0 && !selectedCustomers.includes(customer.crmCustomerName)) {
                return false;
            }

            // Solution filter
            if (selectedSolutions.length > 0 && !selectedSolutions.includes(customer.crmProductName)) {
                return false;
            }

            // CVC Lead filter (uses cvcLead field)
            if (selectedCVCLeads.length > 0 && !selectedCVCLeads.includes(customer.cvcLead)) {
                return false;
            }

            // Customer Assigned PM filter (uses owner field)
            if (selectedCustomerAssignedPMs.length > 0 && !selectedCustomerAssignedPMs.includes(customer.owner)) {
                return false;
            }

            // Last Engaged filter
            if (selectedLastEngaged.length > 0) {
                const bucket = getLastEngagedBucket(customer.modifiedOn);
                if (!selectedLastEngaged.includes(bucket)) return false;
            }

            return true;
        });
    }, [customersResponse, searchQuery, selectedCustomers, selectedSolutions, selectedCVCLeads, selectedCustomerAssignedPMs, selectedLastEngaged]);

    // Calculate metrics
    const metrics = useMemo(() => {
        const customers = filteredCustomers;
        const uniqueCustomerNames = new Set(customers.map(c => c.crmCustomerName));
        const uniqueSolutions = new Set(customers.map(c => c.crmProductName));

        // Count customers with multiple solutions
        const customerSolutionCounts = new Map<string, Set<string>>();
        customers.forEach(c => {
            if (!customerSolutionCounts.has(c.crmCustomerName)) {
                customerSolutionCounts.set(c.crmCustomerName, new Set());
            }
            customerSolutionCounts.get(c.crmCustomerName)?.add(c.crmProductName);
        });
        let customersWithMultipleSolutions = 0;
        customerSolutionCounts.forEach((solutions) => {
            if (solutions.size > 1) {
                customersWithMultipleSolutions++;
            }
        });

        // Count engagements (customer+solution) with multiple notes
        // Each customerProductId can have multiple records if there are multiple notes
        const customerProductNoteCounts = new Map<string, number>();
        customers.forEach(c => {
            const key = c.customerProductId || `${c.crmCustomerName}_${c.crmProductName}`;
            customerProductNoteCounts.set(key, (customerProductNoteCounts.get(key) || 0) + 1);
        });
        let engagementsWithMultipleNotes = 0;
        customerProductNoteCounts.forEach((count) => {
            if (count > 1) {
                engagementsWithMultipleNotes++;
            }
        });

        return {
            totalEngagements: customers.length,
            uniqueCustomers: uniqueCustomerNames.size,
            totalSolutions: uniqueSolutions.size,
            customersWithMultipleSolutions,
            engagementsWithMultipleNotes
        };
    }, [filteredCustomers]);

    // Chart data for CVC Leads
    const cvcLeadChartData = useMemo(() => {
        const counts = new Map<string, number>();
        filteredCustomers.forEach(c => {
            const lead = c.cvcLead || "Unknown";
            counts.set(lead, (counts.get(lead) || 0) + 1);
        });

        const entries: Array<{ stage: string; count: number; color: string }> = [];
        counts.forEach((count, stage) => {
            entries.push({ stage, count, color: "" });
        });
        return entries.sort((a, b) => b.count - a.count).slice(0, 10); // Top 10
    }, [filteredCustomers]);

    // Chart data for Solutions
    const solutionChartData = useMemo(() => {
        const counts = new Map<string, number>();
        filteredCustomers.forEach(c => {
            const solution = c.crmProductName || "Unknown";
            counts.set(solution, (counts.get(solution) || 0) + 1);
        });

        const entries: Array<{ stage: string; count: number; color: string }> = [];
        counts.forEach((count, stage) => {
            entries.push({ stage, count, color: SOLUTION_COLORS[stage] || "" });
        });
        return entries.sort((a, b) => b.count - a.count);
    }, [filteredCustomers]);

    // Chart data for Last Engaged buckets
    const lastEngagedBucketData = useMemo(() => {
        const bucketCounts = new Map<string, number>();
        LAST_ENGAGED_BUCKETS.forEach(b => bucketCounts.set(b.key, 0));

        filteredCustomers.forEach(c => {
            const bucket = getLastEngagedBucket(c.modifiedOn);
            bucketCounts.set(bucket, (bucketCounts.get(bucket) || 0) + 1);
        });

        return LAST_ENGAGED_BUCKETS.map(b => ({
            key: b.key,
            label: b.label,
            fullLabel: b.fullLabel,
            count: bucketCounts.get(b.key) || 0,
            color: b.color
        }));
    }, [filteredCustomers]);

    // Clear all filters
    const clearFilters = useCallback(() => {
        setSelectedCustomers([]);
        setSelectedSolutions([]);
        setSelectedCVCLeads([]);
        setSelectedCustomerAssignedPMs([]);
        setSelectedLastEngaged([]);
        setSearchQuery("");
    }, [setSelectedCustomers, setSelectedSolutions, setSelectedCVCLeads, setSelectedCustomerAssignedPMs, setSelectedLastEngaged]);

    // Handle notes click - gather all notes for this customerProductId from existing data
    const handleNotesClick = useCallback((customer: IPurviewCVCCustomer) => {
        // Open panel and show loading state immediately
        setSelectedCustomerForNotes({
            customerProductId: customer.customerProductId,
            customerName: customer.crmCustomerName,
            productName: customer.crmProductName,
            notesData: []
        });
        setIsLoadingNotes(true);
        openNotesPanel();

        // Simulate loading delay for better UX feedback
        setTimeout(() => {
            // Get all rows for this customer+product combo (each row is a note from the joined query)
            // Use customerProductId if available, otherwise fallback to matching by customerName + productName
            const hasValidCustomerProductId = customer.customerProductId && customer.customerProductId.trim() !== "";

            const allNotesForCustomer = customersResponse?.customers
                .filter(c => {
                    if (hasValidCustomerProductId) {
                        return c.customerProductId === customer.customerProductId;
                    }
                    // Fallback: match by customer name AND product name
                    return c.crmCustomerName === customer.crmCustomerName &&
                        c.crmProductName === customer.crmProductName;
                })
                .map(c => ({
                    notesId: c.notesId,
                    subject: c.subject,
                    notes: c.notes,
                    modifiedOn: c.modifiedOn,
                    modifiedOn1: c.modifiedOn1,
                    modifiedBy1: c.modifiedBy1,
                    modifiedByAlias: c.modifiedByAlias,
                    createdOn: c.createdOn,
                    createdBy: c.createdBy,
                    createdByAlias: c.createdByAlias,
                    latestNote: c.latestNote
                }))
                .filter(n => n.notes) // Only include rows with notes content
                .sort((a, b) => {
                    // Sort by modifiedOn1 descending
                    const dateA = a.modifiedOn1 ? new Date(a.modifiedOn1).getTime() : 0;
                    const dateB = b.modifiedOn1 ? new Date(b.modifiedOn1).getTime() : 0;
                    return dateB - dateA;
                }) || [];

            setSelectedCustomerForNotes({
                customerProductId: customer.customerProductId,
                customerName: customer.crmCustomerName,
                productName: customer.crmProductName,
                notesData: allNotesForCustomer
            });
            setIsLoadingNotes(false);
        }, 1000); // 1 second delay
    }, [openNotesPanel, customersResponse]);

    // Format date for display
    const formatDate = (dateStr: string | null) => {
        if (!dateStr) return "-";
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return "-";
        return date.toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" });
    };

    // Data grid columns
    const columns: GridColDef[] = useMemo(() => [
        {
            field: "crmLink",
            headerName: "CRM Link",
            width: 85,
            sortable: false,
            disableColumnMenu: true,
            renderCell: (params: GridRenderCellParams) => {
                const url = params.row.customerProductURL;
                if (!url) {
                    return <span style={{ color: "#95a5a6" }}>—</span>;
                }
                return (
                    <Link
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={url}
                        style={{
                            color: "rgb(0, 120, 212)",
                            textDecoration: "none",
                            fontSize: 26,
                            cursor: "pointer"
                        }}
                    >
                        <LinkRegular />
                    </Link>
                );
            }
        },
        {
            field: "crmCustomerName",
            headerName: "Customer Name",
            flex: 1.5,
            minWidth: 180,
            renderCell: (params: GridRenderCellParams) => (
                <TooltipHost content={params.value}>
                    <span style={{ fontWeight: 500, color: "#0078D4" }}>{params.value}</span>
                </TooltipHost>
            )
        },
        {
            field: "engagementStatus",
            headerName: "Status",
            width: 130,
            minWidth: 120,
            renderCell: (params: GridRenderCellParams) => (
                params.value && params.value.trim() ? <StatusBadge status={params.value} /> : null
            )
        },
        {
            field: "crmProductName",
            headerName: "Solution",
            width: 220,
            renderCell: (params: GridRenderCellParams) => {
                const color = SOLUTION_COLORS[params.value] || "#0078D4";
                return (
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <div style={{
                            width: "4px",
                            height: "20px",
                            borderRadius: "2px",
                            background: color,
                            flexShrink: 0
                        }} />
                        <TooltipHost content={params.value}>
                            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {params.value}
                            </span>
                        </TooltipHost>
                    </div>
                );
            }
        },
        {
            field: "cvcLead",
            headerName: "CVC Lead",
            width: 150,
            renderCell: (params: GridRenderCellParams) => (
                <TooltipHost content={params.value}>
                    <span>{params.value || "-"}</span>
                </TooltipHost>
            )
        },
        {
            field: "owner",
            headerName: "Customer Assigned PM",
            width: 180,
            renderCell: (params: GridRenderCellParams) => (
                <TooltipHost content={params.value}>
                    <span>{params.value || "-"}</span>
                </TooltipHost>
            )
        },

        {
            field: "lastUpdatedDate",
            headerName: "Last Updated Date",
            width: 130,
            renderCell: (params: GridRenderCellParams) => formatDate(params.value),
            sortComparator: (v1: string | null, v2: string | null) => {
                const date1 = v1 ? new Date(v1).getTime() : 0;
                const date2 = v2 ? new Date(v2).getTime() : 0;
                return date1 - date2;
            }
        },
        {
            field: "createdDate",
            headerName: "Created Date",
            width: 120,
            renderCell: (params: GridRenderCellParams) => formatDate(params.value),
            sortComparator: (v1: string | null, v2: string | null) => {
                const date1 = v1 ? new Date(v1).getTime() : 0;
                const date2 = v2 ? new Date(v2).getTime() : 0;
                return date1 - date2;
            }
        },
        {
            field: "lastNote",
            headerName: "Last Note",
            width: 120,
            renderCell: (params: GridRenderCellParams) => formatDate(params.value),
            sortComparator: (v1: string | null, v2: string | null) => {
                const date1 = v1 ? new Date(v1).getTime() : 0;
                const date2 = v2 ? new Date(v2).getTime() : 0;
                return date1 - date2;
            }
        },
        {
            field: "latestNote",
            headerName: "Weekly Tweet",
            flex: 2,
            minWidth: 250,
            renderCell: (params: GridRenderCellParams) => (
                <TooltipHost content={params.value}>
                    <span style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        display: "block",
                        maxWidth: "100%"
                    }}>
                        {params.value || "-"}
                    </span>
                </TooltipHost>
            )
        },
        {
            field: "notes",
            headerName: "Notes",
            width: 80,
            headerAlign: "center",
            align: "center",
            sortable: false,
            disableColumnMenu: true,
            renderCell: (params: GridRenderCellParams) => (
                <IconButton
                    iconProps={{ iconName: "QuickNote" }}
                    title="View Notes"
                    ariaLabel="View Notes"
                    onClick={() => handleNotesClick(params.row as IPurviewCVCCustomer)}
                    styles={{
                        root: {
                            color: "#0078D4"
                        }
                    }}
                />
            )
        }
    ], [handleNotesClick]);

    // Convert customers to grid rows with unique IDs
    const rowsWithIndex = useMemo(() => {
        return filteredCustomers.map((customer, index) => ({
            ...customer,
            _uniqueRowId: `${customer.customerProductId || ""}_${index}`
        }));
    }, [filteredCustomers]);

    const rows: IRows = {
        values: rowsWithIndex,
        count: rowsWithIndex.length
    };

    const isLoading = externalLoading || isLoadingCustomers;

    if (isLoading) {
        return (
            <div className={PURVIEW_CVC_CUSTOMERS_STYLES.fullPageLoadingContainer}>
                <Spinner size={SpinnerSize.large} label="Loading Purview CVC customers..." />
            </div>
        );
    }

    if (error) {
        return (
            <div className={PURVIEW_CVC_CUSTOMERS_STYLES.errorContainer}>
                <Icon iconName="ErrorBadge" style={{ fontSize: 48, color: "#e74c3c", marginBottom: 16 }} />
                <div style={{ fontSize: 18, fontWeight: 600, color: "#e74c3c", marginBottom: 8 }}>Error Loading Data</div>
                <div style={{ color: "#7f8c8d" }}>Unable to fetch Purview CVC customer data. Please try again later.</div>
            </div>
        );
    }

    return (
        <div className={PURVIEW_CVC_CUSTOMERS_STYLES.tabContainer}>
            {/* Filter Section - Blue gradient card matching BWC style */}
            <div className={PURVIEW_CVC_CUSTOMERS_STYLES.filtersCard}>
                <div className={PURVIEW_CVC_CUSTOMERS_STYLES.filtersTitle}>
                    <Icon iconName="Filter" style={{ marginRight: "8px" }} />
                    Filters
                </div>
                <div className={PURVIEW_CVC_CUSTOMERS_STYLES.filtersRow}>
                    <div className={PURVIEW_CVC_CUSTOMERS_STYLES.filterGroup} style={{ width: "200px", minWidth: "180px" }}>
                        <label className={PURVIEW_CVC_CUSTOMERS_STYLES.filterLabel}>CUSTOMER NAME</label>
                        <SearchableMultiSelect
                            placeholder="Search Customer(s)"
                            options={filterOptions.customers}
                            selectedKeys={selectedCustomers}
                            onChange={setSelectedCustomers}
                        />
                    </div>
                    <div className={PURVIEW_CVC_CUSTOMERS_STYLES.filterGroup}>
                        <label className={PURVIEW_CVC_CUSTOMERS_STYLES.filterLabel}>SOLUTION</label>
                        <SearchableMultiSelect
                            placeholder="Search Solution(s)"
                            options={filterOptions.solutions}
                            selectedKeys={selectedSolutions}
                            onChange={setSelectedSolutions}
                        />
                    </div>
                    <div className={PURVIEW_CVC_CUSTOMERS_STYLES.filterGroup}>
                        <label className={PURVIEW_CVC_CUSTOMERS_STYLES.filterLabel}>CVC LEAD</label>
                        <SearchableMultiSelect
                            placeholder="Search CVC Lead(s)"
                            options={filterOptions.cvcLeads}
                            selectedKeys={selectedCVCLeads}
                            onChange={setSelectedCVCLeads}
                        />
                    </div>
                    <div className={PURVIEW_CVC_CUSTOMERS_STYLES.filterGroup}>
                        <label className={PURVIEW_CVC_CUSTOMERS_STYLES.filterLabel}>CUSTOMER ASSIGNED PM</label>
                        <SearchableMultiSelect
                            placeholder="Search PM(s)"
                            options={filterOptions.customerAssignedPMs}
                            selectedKeys={selectedCustomerAssignedPMs}
                            onChange={setSelectedCustomerAssignedPMs}
                        />
                    </div>
                    <div className={PURVIEW_CVC_CUSTOMERS_STYLES.filterGroup}>
                        <label className={PURVIEW_CVC_CUSTOMERS_STYLES.filterLabel}>LAST ENGAGED</label>
                        <SearchableMultiSelect
                            placeholder="All Buckets"
                            options={LAST_ENGAGED_BUCKETS.map(b => b.key)}
                            selectedKeys={selectedLastEngaged}
                            onChange={setSelectedLastEngaged}
                        />
                    </div>
                    <button className={PURVIEW_CVC_CUSTOMERS_STYLES.clearButton} onClick={clearFilters}>
                        Clear
                    </button>
                </div>
            </div>

            {/* AI Companion Section */}
            <PurviewCVCAICompanionSection
                totalCustomers={metrics.uniqueCustomers}
                totalEngagements={metrics.totalEngagements}
                totalSolutions={metrics.totalSolutions}
            />

            {/* Stats Cards */}
            <div style={{ display: "flex", gap: "16px", flexWrap: "wrap", marginBottom: "16px" }}>
                <StatCard
                    icon="Contact"
                    iconColor="#2980b9"
                    iconBgColor="#e6f2fb"
                    value={metrics.uniqueCustomers}
                    label="Unique Customers"
                />
                <StatCard
                    icon="ProgressRingDots"
                    iconColor="#27ae60"
                    iconBgColor="#e8f5e9"
                    value={metrics.totalEngagements}
                    label="Total Customer Solutions Engagements"
                />
                <StatCard
                    icon="QuickNote"
                    iconColor="#8e44ad"
                    iconBgColor="#f3e5f5"
                    value={metrics.engagementsWithMultipleNotes}
                    label="Engagements with 2+ Notes"
                />
                <StatCard
                    icon="People"
                    iconColor="#e67e22"
                    iconBgColor="#fef3e2"
                    value={metrics.customersWithMultipleSolutions}
                    label="Customers with 2+ Solutions"
                />
            </div>

            {/* Donut Charts */}
            <DonutChartFilters
                cvcLeadData={cvcLeadChartData}
                selectedCVCLeads={selectedCVCLeads}
                onCVCLeadClick={setSelectedCVCLeads}
                solutionData={solutionChartData}
                selectedSolutions={selectedSolutions}
                onSolutionClick={setSelectedSolutions}
                lastEngagedData={lastEngagedBucketData}
                selectedLastEngaged={selectedLastEngaged}
                onLastEngagedClick={setSelectedLastEngaged}
                totalEngagements={metrics.totalEngagements}
            />

            {/* Data Grid */}
            <div className={PURVIEW_CVC_CUSTOMERS_STYLES.tableSection}>
                <CustomDataGrid
                    columns={columns}
                    rows={rows}
                    loading={false}
                    getRowId={(row) => row._uniqueRowId}
                    paginationOptions={{ pageSize: 15, page: 0 }}
                    pageSizeOptions={[15, 25, 50, 75, 100]}
                    kustoLink={customersResponse?.purviewCVCCustomersKustoLink}
                    TabName="Purview CVC Customers"
                    sortingOrder={["asc", "desc"]}
                    enableUrlParams
                />
            </div>

            {/* Notes Panel */}
            {selectedCustomerForNotes && (
                <PurviewCVCNotesPanel
                    isOpen={isNotesPanelOpen}
                    onDismiss={closeNotesPanel}
                    customerProductId={selectedCustomerForNotes.customerProductId}
                    customerName={selectedCustomerForNotes.customerName}
                    productName={selectedCustomerForNotes.productName}
                    notesData={selectedCustomerForNotes.notesData}
                    kustoLink={customersResponse?.purviewCVCCustomersKustoLink}
                    isLoading={isLoadingNotes}
                />
            )}
        </div>
    );
};
